import { buildEntitySheet } from './entitySheetPdfLayout';

export interface DemarcheAnswer { question: string; answer: string }

interface DemarcheAnswersPdfData {
    /** Identifiant du rendez-vous — repris en pied de page comme sur les autres fiches. */
    id: string;
    /** Le client : nom affiché sur la fiche. */
    contactName: string;
    contactEmail?: string;
    contactPhone?: string;
    /**
     * Le référent — la personne qui organise, distincte de la société qui signe. Le bloc « Référent
     * contact » de la fiche est resté vide depuis le premier jour parce que rien ne le remplissait
     * (constaté par Emeni sur son PDF, 2026-08-30) ; il est désormais alimenté par le référent de la
     * fiche CRM. Facultatif : sans référent, la fiche affiche « Aucun référent renseigné. »
     */
    referentName?: string;
    referentEmail?: string;
    referentPhone?: string;
    /** Nom de la Démarche à laquelle il a répondu. */
    demarcheName?: string;
    /** Date et heure du rendez-vous, en clair. */
    appointmentLabel?: string;
    answers: DemarcheAnswer[];
    /** Choix cochés et message libre, quand la Démarche en proposait. */
    choices?: string[];
    freeText?: string;
    logoUrl?: string | null;
    issuerName?: string;
    brandColor?: string;
}

/**
 * Les réponses d'un client à une Démarche, en PDF, depuis son rendez-vous.
 *
 * Demande d'Emeni (2026-08-26) : « je reçois alors son RDV pro + la réponse à toutes les questions en
 * format PDF rattaché au RDV pro ». Les réponses étaient déjà rattachées au rendez-vous et lisibles à
 * l'écran — ce qui manquait, c'était de pouvoir les emporter : les imprimer pour un brief de cuisine, les
 * transmettre à un prestataire, les joindre à un devis établi dans un autre logiciel.
 *
 * Généré dans le navigateur, comme toutes les autres fiches PDF de l'application, et sur la même mise en
 * page (`entitySheetPdfLayout`) : une fiche de plus, pas un format de plus à maintenir.
 *
 * Un déroulé horaire ressort naturellement : il est stocké en texte, une ligne par moment, et la mise en
 * page respecte les retours à la ligne.
 */
export const generateDemarcheAnswersPdf = async (data: DemarcheAnswersPdfData) => {
    try {
        const sections = [];

        if (data.choices && data.choices.length > 0) {
            sections.push({
                title: 'Choix retenus',
                rows: data.choices.map((c) => ({ left: c })),
                asChips: true,
            });
        }

        sections.push({
            title: 'Réponses au questionnaire',
            // La question en colonne gauche, la réponse à droite : c'est la lecture qu'on en fait, question
            // par question. Une réponse vide est conservée et marquée, plutôt que la ligne disparaisse —
            // savoir qu'une question est restée sans réponse fait partie de l'information.
            rows: data.answers.map((a) => ({ left: a.question, right: (a.answer || '').trim() || '—' })),
            emptyLabel: "Cette Démarche ne comportait aucune question.",
        });

        if (data.freeText && data.freeText.trim()) {
            sections.push({
                title: 'Message du client',
                rows: [{ left: data.freeText.trim() }],
            });
        }

        const doc = await buildEntitySheet({
            id: data.id,
            kindLabel: 'Réponses au questionnaire',
            name: data.contactName || 'Client',
            email: data.contactEmail,
            phone: data.contactPhone,
            contact: (data.referentName || data.referentEmail || data.referentPhone)
                ? { name: data.referentName, email: data.referentEmail, phone: data.referentPhone }
                : undefined,
            notes: [data.demarcheName ? `Démarche : ${data.demarcheName}` : '', data.appointmentLabel ? `Rendez-vous : ${data.appointmentLabel}` : '']
                .filter(Boolean).join('\n'),
            logoUrl: data.logoUrl,
            issuerName: data.issuerName,
            brandColor: data.brandColor,
            sections,
        });

        const nomPropre = (data.contactName || 'client').replace(/[^a-zA-Z0-9]/g, '_');
        doc.save(`Reponses_${nomPropre}.pdf`);
    } catch (e) {
        console.error('Demarche answers PDF generation error:', e);
        alert('Erreur lors de la génération du document PDF.');
    }
};
